/*
  TranscriptReviewPane — the transcript-suggestion review card shown at
  the top of AssessmentEditor.

  Renders the filter chips, one row per suggestion with speaker /
  experiencer / laterality / units / confidence badges, Accept /
  Defer / Reject / Report actions, and inline ClassDClarifyForm when
  a row is flagged incomplete (spec TAF-012).

  All state + mutations live in ``useTranscriptSuggestions``; this
  component is a thin presentational layer so future variants
  (e.g. a compact drawer) can share the same hook.
*/
import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Sparkles } from "lucide-react";


/**
 * ClassDClarifyForm — inline structured-metadata capture for
 * Class D "clarify"-state suggestions (spec TAF-012 phase 2). The
 * parent renders this when ``policy_result.validation ===
 * "incomplete"``; the form pre-fills any partial data captured by
 * the LLM and requires the missing bits before submit.
 */
export function ClassDClarifyForm({ suggestion, onSubmit }) {
  const a = suggestion?.assertion || {};
  const key = String(suggestion?.field_key || "").toLowerCase();
  const sideCapable = ["grip", "rom", "sts_side", "force", "circumference"]
    .some((h) => key.includes(h));
  const [units, setUnits] = useState(a.units || "");
  const [laterality, setLaterality] = useState(a.laterality || "unspecified");
  const [setup, setSetup] = useState(a.setup || "");
  const [saving, setSaving] = useState(false);

  const missing = [];
  if (!units.trim()) missing.push("units");
  if (sideCapable && (!laterality || laterality === "unspecified")) missing.push("side");

  async function submit() {
    setSaving(true);
    try {
      await onSubmit({
        units: units.trim(),
        laterality,
        setup: setup.trim(),
      });
    } finally { setSaving(false); }
  }

  return (
    <div className="mt-2 border border-amber-300 bg-amber-50 rounded p-2 space-y-2"
         data-testid={`clarify-form-${suggestion.field_key}`}>
      <div className="text-2xs uppercase tracking-wider text-amber-900 font-semibold">
        Clarify structured metadata
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        <div>
          <Label className="text-2xs">Units *</Label>
          <Input
            value={units}
            onChange={(e) => setUnits(e.target.value)}
            placeholder="kg / mmHg / s / reps"
            className="h-7 text-xs"
            data-testid={`clarify-units-${suggestion.field_key}`}
          />
        </div>
        {sideCapable && (
          <div>
            <Label className="text-2xs">Side *</Label>
            <select
              value={laterality}
              onChange={(e) => setLaterality(e.target.value)}
              className="h-7 text-xs w-full border border-zinc-300 rounded px-2 bg-white"
              data-testid={`clarify-laterality-${suggestion.field_key}`}
            >
              <option value="unspecified">— select —</option>
              <option value="left">left</option>
              <option value="right">right</option>
              <option value="bilateral">bilateral</option>
            </select>
          </div>
        )}
        <div className={sideCapable ? "" : "sm:col-span-2"}>
          <Label className="text-2xs">Setup / tool / position</Label>
          <Input
            value={setup}
            onChange={(e) => setSetup(e.target.value)}
            placeholder="e.g. seated dynamometer"
            className="h-7 text-xs"
            data-testid={`clarify-setup-${suggestion.field_key}`}
          />
        </div>
      </div>
      <div className="flex items-center gap-2">
        <Button
          size="sm"
          className="h-7 bg-amber-600 hover:bg-amber-700 text-white"
          onClick={submit}
          disabled={saving || missing.length > 0}
          data-testid={`clarify-submit-${suggestion.field_key}`}
        >
          {saving ? "Saving…" : missing.length ? `Fill ${missing.join(" + ")}` : "Save clarification"}
        </Button>
        <span className="text-2xs text-amber-800">
          Class D readings need structured metadata before they can be accepted.
        </span>
      </div>
    </div>
  );
}


export default function TranscriptReviewPane({
  suggestions,
  filteredSuggestions,
  speakerFilter,
  experiencerFilter,
  setSpeakerFilter,
  setExperiencerFilter,
  acceptSuggestion,
  rejectSuggestion,
  deferSuggestion,
  clarifySuggestion,
  batchAcceptSafe,
  setEvidenceSuggestion,
  setReportSuggestion,
  readOnly,
}) {
  if (!suggestions || suggestions.length === 0) return null;
  // Hide the review pane on finalised (readOnly) assessments when no
  // suggestion is still pending — nothing can be actioned and the
  // "N PENDING" section badge relies on the same filter. This
  // prevents dozens of already-dismissed rows from confusing the
  // clinician after they finalise. If a proposed row somehow
  // remains, we still render so it isn't invisibly stranded.
  const stillPending = suggestions.some(
    (s) => (s?.review?.status || "proposed") === "proposed",
  );
  if (readOnly && !stillPending) return null;

  return (
    <Card
      className="p-3 border border-violet-200 bg-violet-50 space-y-2"
      data-testid="suggestions-panel"
    >
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-violet-700" />
          <div className="font-semibold text-violet-950 text-sm">
            Transcript suggestions ({suggestions.length}) — review before applying
          </div>
        </div>
        {!readOnly && (
          <Button
            size="sm"
            variant="outline"
            className="h-7 border-violet-300 text-violet-800 hover:bg-violet-100"
            onClick={batchAcceptSafe}
            data-testid="suggestions-batch-accept"
          >
            Accept all safe (Class A)
          </Button>
        )}
      </div>
      {/* Filter row — spec §6 keyboard efficiency. */}
      <div className="flex items-center gap-1 flex-wrap text-2xs">
        <span className="uppercase tracking-wider text-violet-800 font-semibold">Filter:</span>
        {["patient", "clinician", "carer", "unknown"].map((sp) => {
          const active = speakerFilter === sp;
          return (
            <button
              key={sp}
              type="button"
              onClick={() => setSpeakerFilter(active ? "" : sp)}
              className={`px-1.5 py-0.5 rounded border ${
                active
                  ? "border-violet-700 bg-violet-700 text-white"
                  : "border-violet-300 bg-white text-violet-800 hover:bg-violet-100"
              }`}
              data-testid={`suggestion-filter-speaker-${sp}`}
            >
              {sp}
            </button>
          );
        })}
        <span className="text-violet-400 mx-1">·</span>
        {[
          { v: "self", label: "self" },
          { v: "unknown", label: "unknown" },
        ].map((e) => {
          const active = experiencerFilter === e.v;
          return (
            <button
              key={e.v}
              type="button"
              onClick={() => setExperiencerFilter(active ? "" : e.v)}
              className={`px-1.5 py-0.5 rounded border ${
                active
                  ? "border-rose-700 bg-rose-700 text-white"
                  : "border-rose-300 bg-white text-rose-800 hover:bg-rose-100"
              }`}
              title={`Show only suggestions where the referent is ${e.label}`}
              data-testid={`suggestion-filter-experiencer-${e.v}`}
            >
              about: {e.label}
            </button>
          );
        })}
        {(speakerFilter || experiencerFilter) && (
          <button
            type="button"
            onClick={() => {
              setSpeakerFilter("");
              setExperiencerFilter("");
            }}
            className="ml-1 px-1.5 py-0.5 rounded border border-zinc-300 bg-white text-zinc-600 hover:bg-zinc-100"
            data-testid="suggestion-filter-clear"
          >
            clear
          </button>
        )}
        <span className="ml-auto text-violet-700">
          showing <b>{filteredSuggestions.length}</b> of {suggestions.length}
        </span>
      </div>
      <div className="text-xs text-violet-800">
        Class A patient-reported fields only. Safety-critical, medications, diagnoses,
        measurements and clearance never appear here (spec §4). Every accept writes an
        audit entry with speaker + evidence.
      </div>
      <div className="grid gap-1.5">
        {filteredSuggestions.length === 0 && suggestions.length > 0 && (
          <div className="text-xs text-violet-700 italic py-2 text-center"
               data-testid="suggestion-filter-empty">
            No suggestions match the current filter — click a filter chip again to clear.
          </div>
        )}
        {filteredSuggestions.map((s) => {
          const a = s.assertion || {};
          const speakerColour = a.speaker === "patient"
            ? "bg-emerald-100 text-emerald-900 border-emerald-300"
            : a.speaker === "clinician"
            ? "bg-sky-100 text-sky-900 border-sky-300"
            : a.speaker === "carer"
            ? "bg-amber-100 text-amber-900 border-amber-300"
            : "bg-zinc-100 text-zinc-700 border-zinc-300";
          const conflict = s.policy_result?.validation === "conflict";
          const incomplete = s.policy_result?.validation === "incomplete";
          const isClassD = s.policy_result?.class === "D";
          return (
            <div
              key={s.id}
              className={`flex flex-col gap-1 bg-white/80 border rounded p-2 ${
                conflict ? "border-amber-300"
                : incomplete ? "border-amber-400"
                : "border-violet-200"
              }`}
              data-testid={`suggestion-${s.field_key}`}
            >
              <div className="flex items-start gap-2">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span className="text-xs text-violet-800 font-medium">
                      {s.field_label || s.field_key}
                    </span>
                    {s.section_label && (
                      <span className="text-2xs text-zinc-500">
                        · {s.section_label}
                      </span>
                    )}
                    <span className={`text-2xs px-1 rounded border ${speakerColour}`}
                          data-testid={`suggestion-speaker-${s.field_key}`}>
                      {a.speaker || "unknown"}
                    </span>
                    {a.negation && (
                      <span className="text-2xs px-1 rounded border border-rose-300 bg-rose-100 text-rose-900">
                        negated
                      </span>
                    )}
                    {a.experiencer && a.experiencer !== "self" && (
                      <span className="text-2xs px-1 rounded border border-rose-300 bg-rose-100 text-rose-900"
                            title="This statement is about another person — review carefully before accepting."
                            data-testid={`suggestion-experiencer-${s.field_key}`}>
                        about: {String(a.experiencer).replace("_", " ")}
                      </span>
                    )}
                    {a.laterality && a.laterality !== "unspecified" && (
                      <span className="text-2xs px-1 rounded border border-zinc-300 bg-white text-zinc-700"
                            data-testid={`suggestion-laterality-${s.field_key}`}>
                        side: {a.laterality}
                      </span>
                    )}
                    {a.units && (
                      <span className="text-2xs px-1 rounded border border-zinc-300 bg-white text-zinc-700"
                            data-testid={`suggestion-units-${s.field_key}`}>
                        units: {a.units}
                      </span>
                    )}
                    {typeof s.model_metadata?.confidence_score === "number" && (
                      <span className="text-2xs px-1 rounded border border-violet-200 bg-violet-50 text-violet-800"
                            title="AI-reported numeric confidence (0-100%)"
                            data-testid={`suggestion-confidence-score-${s.field_key}`}>
                        {Math.round(s.model_metadata.confidence_score * 100)}%
                      </span>
                    )}
                    {a.certainty && a.certainty !== "stated" && (
                      <span className="text-2xs px-1 rounded border border-zinc-300 bg-zinc-100 text-zinc-700">
                        {a.certainty}
                      </span>
                    )}
                    {conflict && (
                      <span className="text-2xs px-1 rounded border border-amber-300 bg-amber-100 text-amber-900">
                        conflict — existing value
                      </span>
                    )}
                    {incomplete && (
                      <span className="text-2xs px-1 rounded border border-amber-400 bg-amber-100 text-amber-900 inline-flex items-center gap-0.5"
                            title="Class D — needs structured metadata before it can be accepted"
                            data-testid={`suggestion-clarify-chip-${s.field_key}`}>
                        clarify
                      </span>
                    )}
                    {isClassD && !incomplete && (
                      <span className="text-2xs px-1 rounded border border-sky-300 bg-sky-50 text-sky-800">
                        objective
                      </span>
                    )}
                  </div>
                  <div className="text-sm text-zinc-900">
                    → <b>{String(s.proposed_value)}</b>
                  </div>
                  {s.evidence?.text && (
                    <div className="text-xs text-zinc-600 italic mt-0.5">
                      &quot;{s.evidence.text}&quot;
                    </div>
                  )}
                  <div className="text-2xs uppercase tracking-wider text-violet-500 mt-0.5">
                    class {s.policy_result?.class || "?"} · confidence {s.model_metadata?.confidence || "?"}
                  </div>
                </div>
                {!readOnly && (
                  <div className="flex gap-1 shrink-0">
                    <Button size="sm" variant="ghost" className="h-7"
                      onClick={() => setEvidenceSuggestion(s)}
                      data-testid={`suggestion-evidence-${s.field_key}`}
                      title="Open evidence drawer — replay the audio moment this came from"
                    >
                      🔊
                    </Button>
                    <Button size="sm" variant="outline" className="h-7"
                      onClick={() => acceptSuggestion(s)}
                      data-testid={`suggestion-accept-${s.field_key}`}
                    >
                      Accept
                    </Button>
                    <Button size="sm" variant="ghost" className="h-7 text-zinc-500"
                      onClick={() => deferSuggestion(s)}
                      data-testid={`suggestion-defer-${s.field_key}`}
                      title="Defer for later review"
                    >
                      Defer
                    </Button>
                    <Button size="sm" variant="ghost" className="h-7 text-zinc-500"
                      onClick={() => rejectSuggestion(s)}
                      data-testid={`suggestion-reject-${s.field_key}`}
                    >
                      Reject
                    </Button>
                    <Button size="sm" variant="ghost" className="h-7 text-rose-600 hover:text-rose-700 hover:bg-rose-50"
                      onClick={() => setReportSuggestion(s)}
                      data-testid={`suggestion-report-${s.field_key}`}
                      title="Report transcription error — logs feedback for prompt tuning and rejects the suggestion"
                    >
                      Report
                    </Button>
                  </div>
                )}
              </div>
              {incomplete && !readOnly && (
                <ClassDClarifyForm
                  suggestion={s}
                  onSubmit={(patch) => clarifySuggestion(s, patch)}
                />
              )}
            </div>
          );
        })}
      </div>
    </Card>
  );
}
